import { BackLink, Image, Intro, Text } from "components/commons";
import { FormProvider } from "contexts/form-context";
import { FormMode, Path } from "enums";
import locale from "localization";
import BankDepositForm from "./bank-deposit-form";
import initialState from "./bank-deposit-form.state";
import {
  createBankDepositApi,
  getBankDepositByIdApi,
  updateBankDepositByIdApi,
  uploadFileApi,
} from "apis";
import { useCallback, useContext, useMemo, useState } from "react";
import { appendSizeToFilename, combineDateAndTimeInput, formatAmount } from "utils";
import { StationContext, UserContext } from "contexts";
import { useAlert, useApi, useModal, useMountAsync, useRouter } from "hooks";
import { ManDepositSuccess } from "images";
import styles from "./bank-deposit.module.scss";
import { ConfirmModal } from "components/modals";
import { stringToDecimal } from "utils/text.utils";
import ViewBankDeposit from "./view-bank-deposit";
const BankDepositDetailsModule = () => {
  const { station } = useContext(StationContext);
  const { user } = useContext(UserContext);
  const { request: createBankDeposit, loading: isSaving } = useApi({
    api: createBankDepositApi,
    modalError: true,
    handleOwnError: true,
    cleanObject: false,
  });
  const { request: uploadFile, loading: isUploading } = useApi({
    api: uploadFileApi,
  });
  const {
    request: getBankDepositById,
    result: bankDeposit,
    loading: isGetting,
  } = useApi({
    api: getBankDepositByIdApi,
  });
  const { request: updateBankDeposit, loading: isUpdating } = useApi({
    api: updateBankDepositByIdApi,
    cleanObject: false,
  });
  const [form, setForm] = useState(null);
  const { addAlert } = useAlert();
  const successModal = useModal();
  const router = useRouter();
  const depositBankId = router.query?.id;
  const formMode = router.query?.mode || router.query?.pageMode || FormMode.Add;
  const isAdd = formMode === FormMode.Add;
  const isEdit = formMode === FormMode.Edit;
  const isView = formMode === FormMode.View;

  const handleOnSubmit = useCallback(
    async (fields) => {
      let slip = fields?.slip?.value;

      if (slip instanceof File) {
        slip = appendSizeToFilename(slip);

        const formData = new FormData();
        formData.append("file", slip);

        const resUpload = await uploadFile({ body: formData });
        if (!resUpload?.fileUrl) {
          return addAlert({
            title: locale.imageUploadFailed,
            subtitle: locale.imageErrorContent,
            type: "error",
            interval: 10000,
          });
        }

        slip = resUpload.fileUrl;
      }

      const dateTime = combineDateAndTimeInput(fields.date.value, fields.time.value);
      const payload = {
        stationCode: station.stationCode,
        depositedAt: dateTime,
        type: fields.type?.value,
        remarks: fields?.remarks?.value || "",
        amount: stringToDecimal(fields?.amount?.value),
        userId: user?.userId,
        imageUrl: slip || "",
        shiftId: fields?.shiftId?.value,
      };

      const res = isAdd
        ? await createBankDeposit(payload)
        : await updateBankDeposit({ id: depositBankId, ...payload });

      if (!res?.error) {
        successModal.show({
          title: "",
          primary: {
            onClick: () => {
              router.push(Path.BankDeposit);
            },
          },
        });
      }
    },
    [
      addAlert,
      createBankDeposit,
      depositBankId,
      isAdd,
      router,
      station.stationCode,
      successModal,
      updateBankDeposit,
      uploadFile,
      user?.userId,
    ]
  );

  const actionButton = useMemo(() => {
    if (isView) {
      return {
        actionText: locale.editBankDeposit,
        actionOnClick: () => {
          router.push(Path.ViewBankDepositDetails(depositBankId, FormMode.Edit));
        },
      };
    }

    if (isEdit)
      return {
        title: locale.editBankDeposit,
      };

    return {
      title: locale.addBankDeposit,
    };
  }, [depositBankId, isEdit, isView, router]);

  useMountAsync(async () => {
    if (!isAdd) {
      await getBankDepositById({ id: depositBankId }, { useLoading: true });
    }
  });

  return (
    <div>
      <BackLink text={locale.bankDepositLogs} path={Path.BankDeposit} />
      <Intro {...actionButton} />
      <FormProvider
        initialState={useMemo(() => initialState(bankDeposit), [bankDeposit])}
        onChange={(form) => setForm(form)}
        onSubmit={handleOnSubmit}
      >
        {isView ? (
          <ViewBankDeposit bankDeposit={bankDeposit} loading={isGetting} />
        ) : (
          <BankDepositForm
            loading={isSaving || isUploading || isUpdating}
            bankDeposit={bankDeposit}
          />
        )}
      </FormProvider>
      <ConfirmModal {...successModal} closable={false}>
        <div className={styles.successModal}>
          <Image src={ManDepositSuccess} />
          <br />
          <Text strong className={styles.title}>
            {locale.bankDepositSuccess}
          </Text>
          <Text className={styles.message}>
            <locale.Populate
              text={locale.bankDepositSuccessMessage}
              items={[<b>{formatAmount(stringToDecimal(form?.fields?.amount?.value || 0))}</b>]}
            />
          </Text>
          {/* <div className={styles.balanceBox}>
            <Text className={styles.label} subtitle>
              {locale.newSafeDropBalance}
            </Text>
            <Text strong className={styles.amount}>
              {formatAmount(stringToDecimal(form?.fields?.amount?.value || 0))}
            </Text>
          </div> */}
        </div>
      </ConfirmModal>
    </div>
  );
};

export default BankDepositDetailsModule;
